// --- React
import React from 'react';
// --- External UI Libraries
import Typography from '@dtci/spellbook/Typography';
// --- Utils
import { extractErrorMessage } from 'datastage/utils/extractErrorMessage';

/**
 * Job Status Form
 *
 * Features:
 * - Displays job status and task details
 * - Handles various job statuses with appropriate messages
 *
 * @param details
 * @returns {React.JSX.Element|null}
 * @constructor
 */
export const JobStatusForm = ({ details }) => {
    if (!details) return null;

    const { id, spaJobStatus, progressCount, targetCount, errorCount } = details;

    const statusMessages = {
        STARTING: () => (
            <Typography variant="T20" style={{ marginBottom: 8 }}>
                Job <b>{id}</b> is creating tasks and preparing to start
            </Typography>
        ),
        RUNNING: () => (
            <Typography variant="T20" style={{ marginBottom: 8 }}>
                Job <b>{id}</b> progress: <b>{progressCount}</b> out of <b>{targetCount}</b> task(s)
                complete with <b>{errorCount}</b> error(s)
            </Typography>
        ),
        COMPLETE: () => (
            <Typography variant="T20" style={{ marginBottom: 8 }}>
                Job <b>{id}</b> finished with <b>{errorCount}</b> error(s) out of{' '}
                <b>{targetCount}</b> task(s)
            </Typography>
        ),
        FAILED: () => (
            <Typography variant="T20" style={{ marginBottom: 8 }}>
                Job <b>{id}</b> failed!
            </Typography>
        ),
        ABORTED: () => (
            <Typography variant="T20" style={{ marginBottom: 8 }}>
                Job <b>{id}</b> was aborted.
            </Typography>
        ),
    };

    // Determine the message to display based on job status
    const message = statusMessages[spaJobStatus] ? (
        statusMessages[spaJobStatus]()
    ) : (
        <Typography variant="T20" style={{ marginBottom: 8 }}>
            Job <b>{id}</b> status: <b>{spaJobStatus}</b>
        </Typography>
    );

    return (
        <>
            <div
                style={{
                    padding: '20px 24px',
                    background: '#fff',
                    borderRadius: 8,
                    margin: '16px 0 24px 0',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.04)',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 56,
                }}
            >
                <Typography variant="T30" bold style={{ color: '#1976d2', marginBottom: 8 }}>
                    Job Status
                </Typography>
                <div>{message}</div>
            </div>
            {details?.tasks?.length > 0 && (
                <div
                    style={{
                        padding: '20px 24px',
                        background: '#fff',
                        borderRadius: 8,
                        margin: '16px 0 24px 0',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Typography variant="T30" bold style={{ color: '#1976d2', marginBottom: 8 }}>
                        Job Tasks
                    </Typography>
                    {details.tasks.map((task, idx) => (
                        <div
                            key={idx}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                marginBottom: 16,
                                padding: '16px 20px',
                                background: '#fff',
                                borderRadius: 8,
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.04)',
                            }}
                        >
                            <div
                                style={{
                                    minWidth: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    background: '#e0e0e0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    fontSize: 18,
                                    marginRight: 16,
                                }}
                            >
                                {idx + 1}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: 2,
                                    }}
                                >
                                    <Typography variant="T20" bold>
                                        {task.taskStatus}
                                    </Typography>
                                    {task.taskStatus === 'RUNNING' && (
                                        <span
                                            style={{
                                                width: 16,
                                                height: 16,
                                                marginLeft: 8,
                                                border: '2px solid #1976d2',
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                display: 'inline-block',
                                                animation: 'spin 1s linear infinite',
                                                alignSelf: 'center',
                                                marginTop: -16,
                                                verticalAlign: 'middle',
                                            }}
                                        />
                                    )}
                                </div>
                                <Typography variant="T20" style={{ marginBottom: 2, wordBreak: 'break-word' }}>
                                    {task.taskDetails}
                                </Typography>
                                <Typography
                                    variant="T20"
                                    style={{ marginBottom: 2, color: '#c00', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
                                >
                                    {extractErrorMessage(task.statusMessage)}
                                </Typography>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};
